import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import pharmacyService from '../../services/pharmacyService';
import StockTable from '../central-pharmacy/components/StockTable';
import PharmacyDispensationsPage from '../central-pharmacy/pages/PharmacyDispensationsPage';
import { usePharmacy } from '../central-pharmacy/pharmacyContext';
import { LoadingBlock, formatNumber } from '../central-pharmacy/ui';
import '../central-pharmacy/CentralPharmacy.css';
import './LocalPharmacy.css';
import LocalPharmacyStat from './LocalPharmacyStat';
import LocalPharmacyAlertsPanel from './LocalPharmacyAlertsPanel';
import { LOCAL_PHARMACY_COPY, SITE_CARE_MODE_LABELS } from './localPharmacyCopy';

const TABS = {
  STOCK: 'stock',
  DISPENSE: 'dispense',
  ALERTS: 'alerts'
};

export default function LocalPharmacyDashboardPage() {
  const { workSiteId, workSites, reloadNonce, stockLocation, isDutyCabinet } = usePharmacy();
  const [activeTab, setActiveTab] = useState(TABS.STOCK);
  const [openCreateSignal, setOpenCreateSignal] = useState(0);
  const [stockLoading, setStockLoading] = useState(true);
  const [stockRows, setStockRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [alertsData, setAlertsData] = useState(null);

  const workSite = useMemo(
    () => workSites.find((s) => String(s.id) === String(workSiteId)) || null,
    [workSites, workSiteId]
  );

  const careModeLabel = workSite?.site_care_mode
    ? SITE_CARE_MODE_LABELS[workSite.site_care_mode] || workSite.site_care_mode
    : null;

  const alertTotal = Number(alertsData?.summary?.total || 0);
  const stockParams = stockLocation ? { stock_location: stockLocation } : {};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workSiteId) {
        setStockRows([]);
        setPendingCount(0);
        setAlertsData(null);
        setStockLoading(false);
        return;
      }
      setStockLoading(true);
      try {
        const [stockRes, dispRes, alertsRes] = await Promise.all([
          pharmacyService.getSiteStock(workSiteId, {
            site_inventory_only: 'true',
            ...stockParams
          }),
          pharmacyService.listDispensations({ site_id: workSiteId, status: 'PENDING' }),
          pharmacyService.getAlerts({ site_id: workSiteId, ...stockParams })
        ]);
        if (cancelled) return;
        setStockRows(stockRes.success && Array.isArray(stockRes.data) ? stockRes.data : []);
        setPendingCount(
          dispRes.success && Array.isArray(dispRes.data) ? dispRes.data.length : 0
        );
        setAlertsData(alertsRes.success ? alertsRes.data : null);
      } catch {
        if (!cancelled) {
          setStockRows([]);
          setPendingCount(0);
          setAlertsData(null);
        }
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workSiteId, reloadNonce, stockLocation]);

  const totalUnits = stockRows.reduce((acc, row) => acc + Number(row.on_hand || 0), 0);
  const inStockCount = stockRows.filter((row) => Number(row.on_hand || 0) > 0).length;
  const lowStockCount = stockRows.filter((row) => row.stock_status === 'LOW_STOCK').length;
  const outStockCount = stockRows.filter((row) => row.stock_status === 'OUT_OF_STOCK').length;

  const openNewDispense = () => {
    setActiveTab(TABS.DISPENSE);
    setOpenCreateSignal((n) => n + 1);
    window.setTimeout(() => {
      document.getElementById('lp-dispense-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <div className="local-pharmacy">
      <section className="lp-hero" aria-label="En-tête pharmacie locale">
        <div className="lp-hero__inner">
          <div>
            <div className="lp-hero__eyebrow">
              <span className="lp-hero__eyebrow-icon" aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 11H5m14 0a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2m14 0V9a2 2 0 0 0-2-2M5 11V9a2 2 0 0 1 2-2m0 0V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2M7 7h10" />
                </svg>
              </span>
              {isDutyCabinet ? LOCAL_PHARMACY_COPY.BADGE_DUTY : LOCAL_PHARMACY_COPY.BADGE}
            </div>
            <h1 className="lp-hero__title">{workSite?.name || 'Site de travail'}</h1>
          </div>
          <div className="lp-hero__meta">
            <span className="lp-site-pill">
              <span className="lp-site-pill__dot" aria-hidden />
              Site actif
            </span>
            {careModeLabel ? (
              <span className="lp-mode-chip">Mode · {careModeLabel}</span>
            ) : null}
            <Button
              variant="light"
              size="sm"
              className="fw-semibold mt-1"
              onClick={() => {
                setActiveTab(TABS.DISPENSE);
                window.setTimeout(() => {
                  document.getElementById('lp-dispense-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 80);
              }}
              disabled={!workSiteId}
            >
              {pendingCount > 0 ? `Ordonnances en attente (${pendingCount})` : 'File de délivrance'}
            </Button>
          </div>
        </div>
      </section>

      <LocalPharmacyAlertsPanel
        data={alertsData}
        loading={stockLoading}
        compact
        onViewAll={() => setActiveTab(TABS.ALERTS)}
      />

      <div className="lp-stat-grid" role="list">
        <LocalPharmacyStat
          label={LOCAL_PHARMACY_COPY.STAT_ALERTS}
          value={stockLoading ? '…' : formatNumber(alertTotal)}
          tone={alertTotal ? 'alert' : 'green'}
          icon="alert"
        />
        <LocalPharmacyStat
          label={LOCAL_PHARMACY_COPY.STAT_UNITS}
          value={stockLoading ? '…' : formatNumber(totalUnits)}
          tone="green"
          icon="units"
        />
        <LocalPharmacyStat
          label={LOCAL_PHARMACY_COPY.STAT_PRODUCTS}
          value={stockLoading ? '…' : formatNumber(inStockCount)}
          tone="blue"
          icon="products"
        />
        <LocalPharmacyStat
          label={LOCAL_PHARMACY_COPY.STAT_LOW}
          value={stockLoading ? '…' : formatNumber(lowStockCount)}
          tone={lowStockCount ? 'warn' : 'green'}
          icon="low"
        />
        <LocalPharmacyStat
          label={LOCAL_PHARMACY_COPY.STAT_OUT}
          value={stockLoading ? '…' : formatNumber(outStockCount)}
          tone={outStockCount ? 'alert' : 'green'}
          icon="out"
        />
        <LocalPharmacyStat
          label={LOCAL_PHARMACY_COPY.STAT_PENDING}
          value={stockLoading ? '…' : formatNumber(pendingCount)}
          tone="purple"
          icon="pending"
        />
      </div>

      <nav className="lp-tabs" aria-label="Sections pharmacie locale">
        <button
          type="button"
          className={`lp-tab ${activeTab === TABS.ALERTS ? 'is-active' : ''}`}
          onClick={() => setActiveTab(TABS.ALERTS)}
        >
          {LOCAL_PHARMACY_COPY.TAB_ALERTS}
          {alertTotal > 0 ? (
            <span className="lp-tab__badge lp-tab__badge--alert">{alertTotal}</span>
          ) : null}
        </button>
        <button
          type="button"
          className={`lp-tab ${activeTab === TABS.STOCK ? 'is-active' : ''}`}
          onClick={() => setActiveTab(TABS.STOCK)}
        >
          {isDutyCabinet ? LOCAL_PHARMACY_COPY.TAB_STOCK_DUTY : LOCAL_PHARMACY_COPY.TAB_STOCK}
        </button>
        <button
          type="button"
          className={`lp-tab ${activeTab === TABS.DISPENSE ? 'is-active' : ''}`}
          onClick={() => setActiveTab(TABS.DISPENSE)}
        >
          {LOCAL_PHARMACY_COPY.TAB_DISPENSE}
          {pendingCount > 0 ? <span className="lp-tab__badge">{pendingCount}</span> : null}
        </button>
      </nav>

      {activeTab === TABS.ALERTS ? (
        <section className="lp-panel" aria-labelledby="lp-alerts-title">
          <div className="lp-panel__head">
            <div>
              <h2 id="lp-alerts-title" className="lp-panel__title">
                {LOCAL_PHARMACY_COPY.ALERTS_SECTION}
              </h2>
              <p className="lp-panel__desc">
                {isDutyCabinet ? LOCAL_PHARMACY_COPY.ALERTS_HINT_DUTY : LOCAL_PHARMACY_COPY.ALERTS_HINT}
              </p>
            </div>
          </div>
          <div className="lp-panel__body">
            <LocalPharmacyAlertsPanel data={alertsData} loading={stockLoading} />
          </div>
        </section>
      ) : null}

      {activeTab === TABS.STOCK ? (
        <section className="lp-panel" aria-labelledby="lp-stock-title">
          <div className="lp-panel__head">
            <div>
              <h2 id="lp-stock-title" className="lp-panel__title">
                {isDutyCabinet ? LOCAL_PHARMACY_COPY.STOCK_SECTION_DUTY : LOCAL_PHARMACY_COPY.STOCK_SECTION}
              </h2>
              <p className="lp-panel__desc">
                {isDutyCabinet ? LOCAL_PHARMACY_COPY.STOCK_HINT_DUTY : LOCAL_PHARMACY_COPY.STOCK_HINT}
              </p>
            </div>
          </div>
          <div className="lp-panel__body">
            {stockLoading ? (
              <LoadingBlock label="Chargement du stock…" />
            ) : workSiteId && stockRows.length === 0 ? (
              <div className="lp-empty-stock">
                <div className="lp-empty-stock__icon" aria-hidden>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  </svg>
                </div>
                <div className="lp-empty-stock__title">
                  {isDutyCabinet ? LOCAL_PHARMACY_COPY.EMPTY_STOCK_DUTY : LOCAL_PHARMACY_COPY.EMPTY_STOCK}
                </div>
                <p className="lp-empty-stock__desc">
                  {isDutyCabinet
                    ? LOCAL_PHARMACY_COPY.EMPTY_STOCK_DUTY_HINT
                    : 'Le stock sera alimenté par la pharmacie centrale (distribution vers le site).'}
                </p>
              </div>
            ) : workSiteId ? (
              <StockTable
                key={`local-stock-${workSiteId}-${stockLocation || 'MAIN'}-${reloadNonce}`}
                siteId={workSiteId}
                allowAdjust={false}
                siteInventoryOnly
                stockLocation={stockLocation || undefined}
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {activeTab === TABS.DISPENSE ? (
        <section id="lp-dispense-section" className="lp-panel" aria-labelledby="lp-dispense-title">
          <div className="lp-panel__head">
            <div>
              <h2 id="lp-dispense-title" className="lp-panel__title">
                Délivrances patient
              </h2>
              <p className="lp-panel__desc">
                Ordonnances transmises par les médecins · contrôle du stock · délivrance au patient.
              </p>
            </div>
          </div>
          <div className="lp-panel__body lp-panel__body--flush lp-dispense-embed">
            <PharmacyDispensationsPage
              hidePageHeader
              siteScoped
              hideSiteColumn
              embedded
              openCreateSignal={openCreateSignal}
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}
